/**
 * MechanicalUploadModal.js
 * 
 * Upload modal for unified mechanical data upload.
 * Parses Excel files containing multiple mechanical sub-types (Rotating Equipment, Vessels, etc.)
 * and routes rows to appropriate database tables based on the Type column.
 * 
 * Features:
 * - Drag-and-drop or browse file selection
 * - Client-side Excel parsing
 * - Displays results per type (rows inserted/updated)
 * - Shows invalid rows with row numbers for user review
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import ExcelJS from 'exceljs';

const MechanicalUploadModal = React.memo(({ 
  isOpen, 
  onClose, 
  onUploadSuccess, 
  onUploadError, 
  projectId 
}) => {
  const { instance, accounts } = useMsal();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [step, setStep] = useState('select'); // 'select', 'validate', 'confirm', 'uploading', 'results'
  const [parseProgress, setParseProgress] = useState({ message: '', percent: 0 });
  const [uploadResults, setUploadResults] = useState(null);
  const [validTypes, setValidTypes] = useState([]);
  const fileInputRef = useRef(null);

  // Fetch valid mechanical types on mount
  useEffect(() => {
    const fetchValidTypes = async () => {
      if (isOpen && accounts && accounts.length > 0) {
        try {
          const response = await authenticatedApiCall(
            instance,
            accounts,
            'GET',
            '/mechanical/unified-upload/valid-types'
          );
          
          if (response.data.success) {
            setValidTypes(response.data.data || []);
          }
        } catch (error) {
          console.error('Error fetching valid types:', error);
          // Set defaults if API fails
          setValidTypes([
            { key: 'rotating', displayName: 'Rotating Equipment', aliases: ['rotate', 'rotation'] },
            { key: 'vessels', displayName: 'Vessels', aliases: ['vessel', 'tank'] }
          ]);
        }
      }
    };
    
    fetchValidTypes();
  }, [isOpen, instance, accounts]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidExcelFile(file)) {
        setSelectedFile(file);
        parseExcelFile(file);
      } else {
        onUploadError('Please select an Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (isValidExcelFile(file)) {
        setSelectedFile(file);
        parseExcelFile(file);
      } else {
        onUploadError('Please select an Excel file (.xlsx or .xls)');
      }
    }
  };

  const isValidExcelFile = (file) => {
    return (
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls')
    );
  };

  const parseExcelFile = async (file) => {
    setLoading(true);
    setValidationError('');
    setStep('validate');
    setParseProgress({ message: 'Reading file...', percent: 10 });
    
    try {
      const buffer = await file.arrayBuffer();
      
      if (buffer.byteLength === 0) {
        throw new Error('File is empty (0 bytes)');
      }
      
      setParseProgress({ message: 'Parsing Excel structure...', percent: 30 });
      
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);
      
      if (!workbook.worksheets || workbook.worksheets.length === 0) {
        throw new Error('No worksheets found in the Excel file');
      }
      
      const SHEET_TYPE_HINTS = {
        'rotating equipment': 'Rotating Equipment',
        rotating: 'Rotating Equipment',
        vessels: 'Vessels',
        vessel: 'Vessels',
        chutes: 'Chutes',
        chute: 'Chutes',
        conveyors: 'Conveyors',
        conveyor: 'Conveyors',
        skids: 'Skids',
        skid: 'Skids',
        import: '',
      };

      const sheetsToRead = workbook.worksheets.filter((ws) => ws && ws.name);
      if (sheetsToRead.length === 0) {
        throw new Error('No worksheets found in the Excel file');
      }

      setParseProgress({ message: 'Extracting data...', percent: 50 });

      const readSheetRows = (worksheet) => {
        const headerRow = worksheet.getRow(1);
        const headers = [];
        headerRow.eachCell((cell, colNumber) => {
          headers[colNumber - 1] = cell.value ? String(cell.value).trim() : '';
        });
        const sheetHint = SHEET_TYPE_HINTS[String(worksheet.name).toLowerCase().trim()] || '';
        const rows = [];

        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;

          const rowData = {};
          let hasData = false;

          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const header = headers[colNumber - 1];
            if (!header) return;

            let cellValue = cell.value;
            if (cellValue !== null && typeof cellValue === 'object' && cellValue.result !== undefined) {
              cellValue = cellValue.result;
            }

            if (cellValue instanceof Date && !Number.isNaN(cellValue.getTime())) {
              hasData = true;
              rowData[header] = cellValue.toISOString().slice(0, 10);
            } else if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
              hasData = true;
              rowData[header] = typeof cellValue === 'number' || typeof cellValue === 'boolean'
                ? cellValue
                : String(cellValue);
            } else {
              rowData[header] = null;
            }
          });

          if (hasData) {
            const typeVal = rowData.Type || rowData.type || rowData.TYPE;
            if (!typeVal && sheetHint) {
              rowData.Type = sheetHint;
            }
            rowData._sheetName = worksheet.name;
            rows.push(rowData);
          }
        });

        return { headers, rows };
      };

      const importSheet = sheetsToRead.find((ws) => ws.name.toLowerCase() === 'import');
      const parsedSheets = importSheet ? [importSheet] : sheetsToRead;
      const dataRows = [];
      let sawTypeColumn = false;

      parsedSheets.forEach((worksheet) => {
        const { headers, rows } = readSheetRows(worksheet);
        if (headers.some((h) => h && h.toLowerCase() === 'type')) sawTypeColumn = true;
        dataRows.push(...rows);
      });

      if (!sawTypeColumn && !dataRows.some((r) => r.Type || r.type || r._sheetName)) {
        throw new Error('Type column is required. Please ensure your Excel file has a "Type" column (Rotating Equipment, Vessels, Skids, Chutes, Conveyors), or use a sheet named after that type.');
      }

      setParseProgress({ message: 'Processing rows...', percent: 70 });
      
      if (dataRows.length === 0) {
        throw new Error('Excel file must have at least one data row');
      }
      
      setParseProgress({ message: 'Validating data...', percent: 90 });
      
      // Quick validation: count types
      const typeCounts = {};
      let emptyTypeCount = 0;
      
      dataRows.forEach(row => {
        const typeValue = String(row.Type || row.type || row.TYPE || '').trim();
        if (!typeValue) {
          emptyTypeCount++;
        } else {
          typeCounts[typeValue.toLowerCase()] = (typeCounts[typeValue.toLowerCase()] || 0) + 1;
        }
      });
      
      console.log('Type distribution:', typeCounts);
      console.log('Empty type count:', emptyTypeCount);
      
      setParsedData(dataRows);
      setParseProgress({ message: 'Complete!', percent: 100 });
      setStep('confirm');
      setLoading(false);
      
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setValidationError(error.message);
      setStep('select');
      setLoading(false);
      setSelectedFile(null);
      onUploadError(`Failed to parse Excel file: ${error.message}`);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  const handleUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      onUploadError('No data to upload');
      return;
    }
    
    setStep('uploading');
    setLoading(true);
    setUploadProgress(0);
    setUploadStatus('Preparing upload...');
    
    try {
      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }
      
      setUploadProgress(20);
      setUploadStatus('Sending data to server...');
      
      const uploadResponse = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/mechanical/unified-upload/unified-upload',
        {
          projectId: projectId,
          data: parsedData,
          isReimport: false
        }
      );
      
      setUploadProgress(100);
      setUploadStatus('Upload complete!');
      
      if (uploadResponse.data.success) {
        setUploadResults(uploadResponse.data.data);
        setStep('results');
        setLoading(false);
      } else {
        throw new Error(uploadResponse.data.message || 'Upload failed');
      }
      
    } catch (error) {
      console.error('Error uploading data:', error);
      setUploadStatus('Upload failed');
      onUploadError(error.response?.data?.message || error.message || 'Failed to upload data');
      setStep('confirm');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (uploadResults) {
      onUploadSuccess({
        message: 'Mechanical data uploaded successfully',
        results: uploadResults
      });
    }
    resetModal();
  };

  const resetModal = () => {
    setSelectedFile(null);
    setParsedData(null);
    setValidationError('');
    setStep('select');
    setUploadProgress(0);
    setUploadStatus('');
    setDragActive(false);
    setParseProgress({ message: '', percent: 0 });
    setUploadResults(null);
    onClose();
  };

  const handleBack = () => {
    setSelectedFile(null);
    setParsedData(null);
    setValidationError('');
    setStep('select');
    setParseProgress({ message: '', percent: 0 });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: step === 'results' ? '700px' : '550px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            Upload Mechanical Data
            {step === 'validate' && ' - Validating...'}
            {step === 'confirm' && ' - Review & Confirm'}
            {step === 'uploading' && ' - Uploading...'}
            {step === 'results' && ' - Complete'}
          </h3>
          <button
            onClick={handleClose}
            disabled={loading && step !== 'results'}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: loading && step !== 'results' ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: loading && step !== 'results' ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* Step 1: File Selection */}
        {step === 'select' && (
          <>
            <div
              style={{
                border: `2px dashed ${dragActive ? '#2E7D32' : '#ddd'}`,
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: dragActive ? '#e8f5e9' : '#f8f9fa',
                transition: 'all 0.2s ease',
                marginBottom: '1.5rem'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚙️</div>
              
              {selectedFile ? (
                <div>
                  <p style={{ margin: '0 0 1rem 0', color: '#2E7D32', fontWeight: 'bold' }}>
                    File selected: {selectedFile.name}
                  </p>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
                    {dragActive ? 'Drop your Excel file here' : 'Drag and drop your Excel file here'}
                  </p>
                  <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
                    or
                  </p>
                  <button
                    onClick={handleBrowseClick}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#2E7D32',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Browse Files
                  </button>
                  <p style={{ margin: '1rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                    Supports .xlsx and .xls files
                  </p>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div style={{
              backgroundColor: '#e8f5e9',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#1b5e20'
            }}>
              <strong>Requirements:</strong>
              <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                <li>Excel file should have a sheet named <strong>"Import"</strong> (or data in the first sheet)</li>
                <li>A <strong>Type</strong> column is required to specify equipment type</li>
                <li>Valid Type values: {validTypes.map(t => t.displayName).join(', ')}</li>
                <li>Rows with the same Type are grouped and inserted into the appropriate table</li>
                <li>Rows with invalid or empty Type values will be reported</li>
              </ul>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </>
        )}

        {/* Step 2: Validation in progress */}
        {step === 'validate' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              {parseProgress.message || 'Validating Excel file structure...'}
            </p>
            {parseProgress.percent > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${parseProgress.percent}%`,
                    height: '100%',
                    backgroundColor: '#2E7D32',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  {parseProgress.percent}% complete
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Confirm upload */}
        {step === 'confirm' && parsedData && (
          <>
            <div style={{
              backgroundColor: '#e8f5e9',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                margin: 0,
                fontWeight: 'bold',
                color: '#2E7D32'
              }}>
                ✓ File parsed successfully! Found {parsedData.length.toLocaleString()} data rows
              </p>
            </div>

            {/* Type preview */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              border: '1px solid #ddd'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#333' }}>
                Type Distribution Preview
              </h4>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                {(() => {
                  const typeCounts = {};
                  let emptyCount = 0;
                  
                  parsedData.forEach(row => {
                    const typeValue = row.Type || row.type || row.TYPE || '';
                    if (!typeValue || typeValue.trim() === '') {
                      emptyCount++;
                    } else {
                      const key = typeValue.trim();
                      typeCounts[key] = (typeCounts[key] || 0) + 1;
                    }
                  });
                  
                  return (
                    <>
                      {Object.entries(typeCounts).map(([type, count]) => (
                        <div key={type} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.25rem 0',
                          borderBottom: '1px solid #eee'
                        }}>
                          <span>{type}</span>
                          <span style={{ fontWeight: 'bold' }}>{count.toLocaleString()} rows</span>
                        </div>
                      ))}
                      {emptyCount > 0 && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.25rem 0',
                          color: '#dc3545'
                        }}>
                          <span>⚠️ Empty Type</span>
                          <span style={{ fontWeight: 'bold' }}>{emptyCount.toLocaleString()} rows</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div style={{
              backgroundColor: '#fff3cd',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#856404'
            }}>
              <strong>⚠️ Note:</strong> Rows with invalid or empty Type values will be skipped and reported in the results.
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Back
              </button>
              <button
                onClick={handleUpload}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#2E7D32',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Upload {parsedData.length.toLocaleString()} Rows
              </button>
            </div>
          </>
        )}

        {/* Step 4: Upload Progress */}
        {step === 'uploading' && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.5rem'
            }}>
              <span style={{ fontSize: '0.9rem', color: '#666' }}>{uploadStatus}</span>
              <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{uploadProgress}%</span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: uploadProgress === 100 ? '#2E7D32' : '#4CAF50',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        )}

        {/* Step 5: Results */}
        {step === 'results' && uploadResults && (
          <>
            <div style={{
              backgroundColor: '#e8f5e9',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                margin: 0,
                fontWeight: 'bold',
                color: '#2E7D32',
                fontSize: '1.1rem'
              }}>
                ✓ Upload Complete!
              </p>
            </div>

            {/* Results per type */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              border: '1px solid #ddd'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#333' }}>
                Results by Type
              </h4>
              {Object.entries(uploadResults.results).map(([typeKey, result]) => (
                <div key={typeKey} style={{
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ fontWeight: 'bold', color: '#2E7D32', marginBottom: '0.25rem' }}>
                    {result.displayName}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>
                    {result.insertedCount > 0 && <span style={{ marginRight: '1rem' }}>✓ {result.insertedCount} inserted</span>}
                    {result.updatedCount > 0 && <span style={{ marginRight: '1rem' }}>✓ {result.updatedCount} updated</span>}
                    {result.skippedCount > 0 && <span style={{ color: '#ffc107' }}>⚠ {result.skippedCount} skipped</span>}
                  </div>
                  {result.errors && result.errors.length > 0 && (
                    <div style={{ fontSize: '0.85rem', color: '#dc3545', marginTop: '0.5rem' }}>
                      {result.errors.slice(0, 3).map((err, i) => (
                        <div key={i}>Row {err.rowNumber}: {err.error}</div>
                      ))}
                      {result.errors.length > 3 && <div>...and {result.errors.length - 3} more errors</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Invalid type rows */}
            {uploadResults.invalidTypeRows && uploadResults.invalidTypeRows.length > 0 && (
              <div style={{
                backgroundColor: '#fff3cd',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1.5rem',
                border: '1px solid #ffc107'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '0.75rem', color: '#856404' }}>
                  ⚠️ Rows with Invalid Type ({uploadResults.invalidTypeRows.length})
                </h4>
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  fontSize: '0.85rem',
                  color: '#856404'
                }}>
                  {uploadResults.invalidTypeRows.slice(0, 20).map((row, i) => (
                    <div key={i} style={{ padding: '0.25rem 0', borderBottom: '1px solid #ffe69c' }}>
                      <strong>Row {row.rowNumber}:</strong> {row.reason}
                    </div>
                  ))}
                  {uploadResults.invalidTypeRows.length > 20 && (
                    <div style={{ marginTop: '0.5rem', fontWeight: 'bold' }}>
                      ...and {uploadResults.invalidTypeRows.length - 20} more
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                  <strong>Valid Type values:</strong> {uploadResults.validTypeValues.join(', ')}
                </div>
              </div>
            )}

            {/* Summary */}
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#1976d2'
            }}>
              <strong>Summary:</strong> {uploadResults.totalProcessed} of {uploadResults.totalRowsReceived} rows processed successfully
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#2E7D32',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default MechanicalUploadModal;
